import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { ContentStatusBadge } from "~/components/admin/content-status-badge";
import { FaqEditor } from "~/components/admin/faq-editor";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RichTextEditor } from "~/components/ui/rich-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  approveGuideFn,
  archiveGuideFn,
  createGuideDraftFn,
  guideQueryKeys,
  guideQueryOptions,
  promoteGuideDraftFn,
  publishGuideFn,
  restoreGuideFn,
  returnGuideToDraftFn,
  updateGuideFn,
  type GuideRow,
} from "~/lib/functions/cih/guides";
import {
  GUIDE_TYPE_OPTIONS,
  LIBRARY_SHELF_OPTIONS,
  PRACTICE_STAGE_BAND_OPTIONS,
  PRACTICE_STAGE_OPTIONS,
  PRCPR_LINK_OPTIONS,
  SERIES_FAMILY_OPTIONS,
  type GuideType,
  type PracticeStage,
} from "~/lib/shared/content-status";

/** Sentinel for a Select whose underlying column is nullable. */
const NONE = "__none__";

/** `<Input type="date">` wants `yyyy-mm-dd`; the column is a timestamptz. */
function toDateInputValue(value: Date | null): string {
  return value ? new Date(value).toISOString().slice(0, 10) : "";
}

/** Empty (or non-numeric) input clears the field back to null — never 0/NaN. */
function toNullableInt(raw: string): number | null {
  if (raw.trim() === "") return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

export const Route = createFileRoute("/admin/cih/guides/$guideId")({
  staticData: { title: "Edit Guide" },
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(
      guideQueryOptions(params.guideId),
    );
    return { title: data?.title ?? "Edit Guide" };
  },
  component: GuideEditorPage,
});

function GuideEditorPage() {
  const { guideId } = Route.useParams();
  const { data: guide, isLoading } = useQuery(guideQueryOptions(guideId));

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Guide not found.</div>
      </div>
    );
  }

  return <GuideEditorForm guideId={guideId} guide={guide} />;
}

function GuideEditorForm({
  guideId,
  guide,
}: {
  guideId: string;
  guide: GuideRow;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState("");

  const form = useForm({
    defaultValues: {
      title: guide.title,
      description: guide.description,
      metaTitle: guide.metaTitle,
      guideType: (guide.guideType as GuideType) ?? "stage_guide",
      practiceStage: (guide.practiceStage as PracticeStage) ?? "all",
      heroImageUrl: guide.heroImageUrl,
      body: guide.body != null ? JSON.stringify(guide.body) : "",
      faqs: (guide.faqs ?? []) as { question: string; answer: string }[],
      pullQuote: guide.pullQuote,
      pullQuoteAttribution: guide.pullQuoteAttribution,
      reviewerName: guide.reviewerName,
      reviewerCredentials: guide.reviewerCredentials,
      reviewedDate: guide.reviewedDate,
      practiceStageBand: guide.practiceStageBand,
      ctaStageBand: guide.ctaStageBand,
      prcprPrimary: guide.prcprPrimary,
      prcprLinks: guide.prcprLinks ?? [],
      category: guide.category,
      shelf: guide.shelf,
      curriculumOrder: guide.curriculumOrder,
      seriesFamily: guide.seriesFamily,
      seriesPosition: guide.seriesPosition,
      lastTaxonomyReview: toDateInputValue(guide.lastTaxonomyReview),
    },
  });

  const showSuccess = (msg: string): void => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: guideQueryKeys.all });
    queryClient.invalidateQueries({
      queryKey: guideQueryKeys.detail(guideId),
    });
  };

  const saveAll = async () => {
    const v = form.state.values;
    await updateGuideFn({
      data: {
        id: guideId,
        title: v.title,
        description: v.description,
        metaTitle: v.metaTitle,
        guideType: v.guideType,
        practiceStage: v.practiceStage,
        heroImageUrl: v.heroImageUrl,
        body: v.body,
        faqs: v.faqs,
        pullQuote: v.pullQuote,
        pullQuoteAttribution: v.pullQuoteAttribution,
        reviewerName: v.reviewerName,
        reviewerCredentials: v.reviewerCredentials,
        reviewedDate: v.reviewedDate,
        practiceStageBand: v.practiceStageBand,
        ctaStageBand: v.ctaStageBand,
        prcprPrimary: v.prcprPrimary,
        prcprLinks: v.prcprLinks.length > 0 ? v.prcprLinks : null,
        category: v.category,
        shelf: v.shelf,
        curriculumOrder: v.curriculumOrder,
        seriesFamily: v.seriesFamily,
        seriesPosition: v.seriesPosition,
        lastTaxonomyReview: v.lastTaxonomyReview
          ? new Date(v.lastTaxonomyReview)
          : null,
      },
    });
  };

  const updateMutation = useMutation({
    mutationFn: saveAll,
    onSuccess: () => {
      invalidate();
      showSuccess("Guide saved.");
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (data: { data: { id: string } }) => {
      await saveAll();
      return publishGuideFn(data);
    },
    onSuccess: () => {
      invalidate();
      showSuccess("Guide published.");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: archiveGuideFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Guide archived.");
    },
  });

  const createDraftMutation = useMutation({
    mutationFn: createGuideDraftFn,
    onSuccess: (newDraft) => {
      invalidate();
      navigate({
        to: "/admin/cih/guides/$guideId",
        params: { guideId: newDraft.id },
      });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async (data: { data: { draftId: string } }) => {
      await saveAll();
      return promoteGuideDraftFn(data);
    },
    onSuccess: (promoted) => {
      queryClient.removeQueries({
        queryKey: guideQueryKeys.detail(guideId),
      });
      invalidate();
      navigate({
        to: "/admin/cih/guides/$guideId",
        params: { guideId: promoted.id },
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: approveGuideFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Guide approved and published.");
    },
  });

  const returnToDraftMutation = useMutation({
    mutationFn: returnGuideToDraftFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Guide returned to draft.");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: restoreGuideFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Guide restored to draft.");
    },
  });

  const handleSave = (): void => {
    updateMutation.mutate();
  };

  const handleReturnToDraft = (): void => {
    const note = window.prompt("Feedback note for the author:");
    if (!note) return;
    returnToDraftMutation.mutate({ data: { id: guideId, note } });
  };

  const anyPending =
    updateMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending ||
    createDraftMutation.isPending ||
    promoteMutation.isPending ||
    approveMutation.isPending ||
    returnToDraftMutation.isPending ||
    restoreMutation.isPending;

  const mutationError =
    updateMutation.error ??
    publishMutation.error ??
    archiveMutation.error ??
    createDraftMutation.error ??
    promoteMutation.error ??
    approveMutation.error ??
    returnToDraftMutation.error ??
    restoreMutation.error;

  const isDraft = guide.status === "draft";
  const isPublished = guide.status === "published";
  const isInReview = guide.status === "in_review";
  const isArchived = guide.status === "archived";
  const hasDraftParent = guide.draftOf != null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-medium text-gray-900">
                {guide.title}
              </h1>
              <ContentStatusBadge status={guide.status} />
            </div>
            <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
              <span>Guide editor — ID: {guideId}</span>
              {guide.slug && guide.status === "published" && (
                <a
                  href={`/coaching-business/${guide.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                >
                  View public page
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {isDraft && !hasDraftParent && (
              <>
                <Button
                  variant="default"
                  onClick={handleSave}
                  disabled={anyPending}
                  loading={updateMutation.isPending}
                >
                  Save
                </Button>
                <Button
                  variant="primary"
                  onClick={() =>
                    publishMutation.mutate({ data: { id: guideId } })
                  }
                  disabled={anyPending}
                  loading={publishMutation.isPending}
                >
                  Publish
                </Button>
              </>
            )}
            {isDraft && hasDraftParent && (
              <Button
                variant="default"
                onClick={handleSave}
                disabled={anyPending}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
            )}
            {isPublished && (
              <>
                <Button
                  variant="default"
                  onClick={() =>
                    createDraftMutation.mutate({
                      data: { publishedId: guideId },
                    })
                  }
                  disabled={anyPending}
                  loading={createDraftMutation.isPending}
                >
                  Create Draft
                </Button>
                <Button
                  variant="destructive"
                  onClick={() =>
                    archiveMutation.mutate({ data: { id: guideId } })
                  }
                  disabled={anyPending}
                  loading={archiveMutation.isPending}
                >
                  Archive
                </Button>
              </>
            )}
            {isInReview && (
              <>
                <Button
                  variant="primary"
                  onClick={() =>
                    approveMutation.mutate({ data: { id: guideId } })
                  }
                  disabled={anyPending}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="default"
                  onClick={handleReturnToDraft}
                  disabled={anyPending}
                  loading={returnToDraftMutation.isPending}
                >
                  Return to Draft
                </Button>
              </>
            )}
            {isArchived && (
              <Button
                variant="default"
                onClick={() =>
                  restoreMutation.mutate({ data: { id: guideId } })
                }
                disabled={anyPending}
                loading={restoreMutation.isPending}
              >
                Restore to Draft
              </Button>
            )}
          </div>
        </div>

        {/* Draft-of banner */}
        {hasDraftParent && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-700">
              Editing draft of published content. Save your changes, then
              promote when ready.
            </p>
            <Button
              size="sm"
              onClick={() =>
                promoteMutation.mutate({ data: { draftId: guideId } })
              }
              disabled={anyPending}
              loading={promoteMutation.isPending}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              Promote to Live
            </Button>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {mutationError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {mutationError instanceof Error
              ? mutationError.message
              : "An error occurred. Please try again."}
          </div>
        )}

        {/* Form */}
        <Card className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="title">
              {(field) => (
                <div>
                  <Label htmlFor="guide-title" className="mb-1">
                    Title
                  </Label>
                  <Input
                    id="guide-title"
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                </div>
              )}
            </form.Field>
            <div>
              <Label htmlFor="guide-slug" className="mb-1">
                Slug
              </Label>
              <Input
                id="guide-slug"
                type="text"
                value={guide.slug ?? ""}
                readOnly
                className="bg-gray-50"
              />
            </div>
          </div>

          <form.Field name="metaTitle">
            {(field) => (
              <div>
                <Label htmlFor="guide-meta-title" className="mb-1">
                  Meta title{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                </Label>
                <Input
                  id="guide-meta-title"
                  type="text"
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="SEO title override"
                />
              </div>
            )}
          </form.Field>

          <div className="grid grid-cols-3 gap-4">
            <form.Field name="guideType">
              {(field) => (
                <div>
                  <Label htmlFor="guide-type" className="mb-1">
                    Guide type
                  </Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(val) =>
                      field.handleChange(val as GuideType)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GUIDE_TYPE_OPTIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
            <form.Field name="practiceStage">
              {(field) => (
                <div>
                  <Label htmlFor="guide-practice-stage" className="mb-1">
                    Practice stage
                  </Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(val) =>
                      field.handleChange(val as PracticeStage)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRACTICE_STAGE_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          </div>

          <form.Field name="heroImageUrl">
            {(field) => (
              <div>
                <Label htmlFor="guide-hero" className="mb-1">
                  Hero image URL{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                </Label>
                <Input
                  id="guide-hero"
                  type="text"
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="https://..."
                />
              </div>
            )}
          </form.Field>

          <form.Field name="description">
            {(field) => (
              <div>
                <Label htmlFor="guide-description" className="mb-1">
                  Description
                </Label>
                <Textarea
                  id="guide-description"
                  rows={3}
                  value={field.state.value}
                  onValueChange={field.handleChange}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="body">
            {(field) => (
              <div>
                <Label className="mb-1">Body</Label>
                <RichTextEditor
                  content={field.state.value}
                  onChange={field.handleChange}
                />
              </div>
            )}
          </form.Field>
        </Card>

        {/* Narrative */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Pull Quote
          </h2>
          <form.Field name="pullQuote">
            {(field) => (
              <div>
                <Label htmlFor="guide-pull-quote" className="mb-1">
                  Pull quote
                </Label>
                <Textarea
                  id="guide-pull-quote"
                  rows={3}
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="One sentence that captures the guide's essence"
                />
              </div>
            )}
          </form.Field>
          <form.Field name="pullQuoteAttribution">
            {(field) => (
              <div className="mt-4">
                <Label htmlFor="guide-pull-quote-attr" className="mb-1">
                  Pull quote attribution
                </Label>
                <Input
                  id="guide-pull-quote-attr"
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="e.g., Guide Author"
                />
              </div>
            )}
          </form.Field>
        </Card>

        {/* Reviewer byline */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Reviewer Byline
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="reviewerName">
              {(field) => (
                <div>
                  <Label htmlFor="guide-reviewer-name" className="mb-1">
                    Reviewer name
                  </Label>
                  <Input
                    id="guide-reviewer-name"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="e.g., Dr. Jane Smith"
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="reviewerCredentials">
              {(field) => (
                <div>
                  <Label htmlFor="guide-reviewer-credentials" className="mb-1">
                    Reviewer credentials
                  </Label>
                  <Input
                    id="guide-reviewer-credentials"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="e.g., PCC, MBA"
                  />
                </div>
              )}
            </form.Field>
          </div>
          <form.Field name="reviewedDate">
            {(field) => (
              <div className="mt-4">
                <Label htmlFor="guide-reviewed-date" className="mb-1">
                  Reviewed date
                </Label>
                <Input
                  id="guide-reviewed-date"
                  type="text"
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="e.g., June 2026"
                />
              </div>
            )}
          </form.Field>
        </Card>

        {/* PrcPR taxonomy */}
        <Card className="mt-6">
          <h2 className="mb-1 text-sm font-medium tracking-wide text-gray-500 uppercase">
            PrcPR Taxonomy
          </h2>
          <p className="mb-4 text-sm text-gray-500">
            Where this guide sits on the coach acquisition chain, and which
            revenue band it is written for.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="practiceStageBand">
              {(field) => (
                <div>
                  <Label htmlFor="guide-practice-stage-band" className="mb-1">
                    Practice stage band
                  </Label>
                  <Select
                    value={field.state.value ?? NONE}
                    onValueChange={(val) =>
                      field.handleChange(
                        PRACTICE_STAGE_BAND_OPTIONS.find(
                          (band) => band.value === val,
                        )?.value ?? null,
                      )
                    }
                  >
                    <SelectTrigger
                      id="guide-practice-stage-band"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not set</SelectItem>
                      {PRACTICE_STAGE_BAND_OPTIONS.map((band) => (
                        <SelectItem key={band.value} value={band.value}>
                          {band.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
            <form.Field name="ctaStageBand">
              {(field) => (
                <div>
                  <Label htmlFor="guide-cta-stage-band" className="mb-1">
                    CTA stage band
                  </Label>
                  <Select
                    value={field.state.value ?? NONE}
                    onValueChange={(val) =>
                      field.handleChange(
                        PRACTICE_STAGE_BAND_OPTIONS.find(
                          (band) => band.value === val,
                        )?.value ?? null,
                      )
                    }
                  >
                    <SelectTrigger id="guide-cta-stage-band" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not set</SelectItem>
                      {PRACTICE_STAGE_BAND_OPTIONS.map((band) => (
                        <SelectItem key={band.value} value={band.value}>
                          {band.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-gray-400">
                    Picks the Coach Business Builder box at the foot of the
                    page. Not set or All shows the 6-Figure Readiness Score.
                  </p>
                </div>
              )}
            </form.Field>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <form.Field name="prcprPrimary">
              {(field) => (
                <div>
                  <Label htmlFor="guide-prcpr-primary" className="mb-1">
                    Primary chain link
                  </Label>
                  <Select
                    value={field.state.value ?? NONE}
                    onValueChange={(val) =>
                      field.handleChange(
                        PRCPR_LINK_OPTIONS.find((link) => link.value === val)
                          ?.value ?? null,
                      )
                    }
                  >
                    <SelectTrigger id="guide-prcpr-primary" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not set</SelectItem>
                      {PRCPR_LINK_OPTIONS.map((link) => (
                        <SelectItem key={link.value} value={link.value}>
                          {link.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
            <form.Field name="category">
              {(field) => (
                <div>
                  <Label htmlFor="guide-category" className="mb-1">
                    Category
                  </Label>
                  <Input
                    id="guide-category"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="e.g., Marketing"
                  />
                </div>
              )}
            </form.Field>
          </div>

          <form.Field name="prcprLinks">
            {(field) => (
              <div className="mt-4">
                <Label className="mb-2">All chain links touched</Label>
                <div className="grid grid-cols-3 gap-2">
                  {PRCPR_LINK_OPTIONS.map((link) => (
                    <Checkbox
                      key={link.value}
                      id={`guide-prcpr-link-${link.value}`}
                      checked={field.state.value.includes(link.value)}
                      onCheckedChange={(checked) =>
                        field.handleChange(
                          checked === true
                            ? [...field.state.value, link.value]
                            : field.state.value.filter((v) => v !== link.value),
                        )
                      }
                    >
                      {link.label}
                    </Checkbox>
                  ))}
                </div>
              </div>
            )}
          </form.Field>

          <div className="mt-6 border-t border-gray-100 pt-6">
            <h3 className="mb-1 text-sm font-medium text-gray-700">
              Library placement
            </h3>
            <p className="mb-4 text-sm text-gray-500">
              Shelf, curriculum order, and series are normally owned by the
              Knowledge Layer Mapping sheet and backfilled from it — a manual
              edit here will be overwritten by the next backfill run unless the
              sheet is updated too.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <form.Field name="shelf">
                {(field) => (
                  <div>
                    <Label htmlFor="guide-shelf" className="mb-1">
                      Shelf
                    </Label>
                    <Select
                      value={field.state.value ?? NONE}
                      onValueChange={(val) =>
                        field.handleChange(
                          LIBRARY_SHELF_OPTIONS.find((s) => s.value === val)
                            ?.value ?? null,
                        )
                      }
                    >
                      <SelectTrigger id="guide-shelf" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Not set</SelectItem>
                        {LIBRARY_SHELF_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
              <form.Field name="curriculumOrder">
                {(field) => (
                  <div>
                    <Label htmlFor="guide-curriculum-order" className="mb-1">
                      Curriculum order{" "}
                      <span className="font-normal text-gray-400">
                        (within shelf)
                      </span>
                    </Label>
                    <Input
                      id="guide-curriculum-order"
                      type="number"
                      min={1}
                      step={1}
                      value={field.state.value ?? ""}
                      onChange={(e) =>
                        field.handleChange(toNullableInt(e.target.value))
                      }
                      placeholder="e.g., 3"
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <form.Field name="seriesFamily">
                {(field) => (
                  <div>
                    <Label htmlFor="guide-series-family" className="mb-1">
                      Series
                    </Label>
                    <Select
                      value={field.state.value ?? NONE}
                      onValueChange={(val) =>
                        field.handleChange(
                          SERIES_FAMILY_OPTIONS.find((s) => s.value === val)
                            ?.value ?? null,
                        )
                      }
                    >
                      <SelectTrigger
                        id="guide-series-family"
                        className="w-full"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Not set</SelectItem>
                        {SERIES_FAMILY_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
              <form.Field name="seriesPosition">
                {(field) => (
                  <div>
                    <Label htmlFor="guide-series-position" className="mb-1">
                      Series position
                    </Label>
                    <Input
                      id="guide-series-position"
                      type="number"
                      min={1}
                      step={1}
                      value={field.state.value ?? ""}
                      onChange={(e) =>
                        field.handleChange(toNullableInt(e.target.value))
                      }
                      placeholder="e.g., 2"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Only meaningful when a series is selected above.
                    </p>
                  </div>
                )}
              </form.Field>
            </div>
          </div>

          <form.Field name="lastTaxonomyReview">
            {(field) => (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="guide-last-taxonomy-review" className="mb-1">
                    Last taxonomy review
                  </Label>
                  <Input
                    id="guide-last-taxonomy-review"
                    type="date"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              </div>
            )}
          </form.Field>
        </Card>

        {/* FAQs */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            FAQs
          </h2>
          <form.Field name="faqs">
            {(field) => (
              <FaqEditor
                value={field.state.value}
                onChange={field.handleChange}
              />
            )}
          </form.Field>
        </Card>

        {/* Sticky save for draft */}
        {isDraft && (
          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={anyPending}
              loading={updateMutation.isPending}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
